import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const managerItems = [
  { label: '质量趋势', icon: 'ri-bar-chart-grouped-line', desc: '一所学校、一个地区、一批课程的整体质量态势' },
  { label: '资源配置', icon: 'ri-pie-chart-2-line', desc: '哪些课程发展稳定，哪些主题需要加强' },
  { label: '区域治理', icon: 'ri-shield-check-line', desc: '分档比例、质量地图和趋势预警' },
];

const teacherItems = [
  { label: '有效方法', icon: 'ri-lightbulb-flash-line', desc: '哪些教学设计有效，哪些方法值得推广' },
  { label: '经验迁移', icon: 'ri-share-forward-line', desc: '个人经验如何变成可复制的教研资源' },
  { label: '成长提升', icon: 'ri-line-chart-line', desc: '证据卡、案例建议和成长跟踪' },
];

export default function Section6DualView({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-24 left-20 w-72 h-72 rounded-full bg-primary-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 right-16 w-80 h-80 rounded-full bg-accent-100/6 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-10 md:mb-12">
          <span
            className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="gold-dot" />应用视角
          </span>
          <h2
            className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            管理者与优秀教师的<span className="text-primary-600">共同视角</span>
          </h2>
          <p
            className={`text-base text-foreground-500 font-body max-w-xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            一个系统同时服务"看全局"和"看方法"两件事，这就是它的应用价值。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">

          {/* 左侧配图 */}
          <div
            className={`lg:col-span-5 transition-all duration-1000 delay-300 ${
              titleVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="rounded-2xl overflow-hidden h-full relative border border-background-200/30 shadow-sm bg-white">
              <img
                src="https://readdy.ai/api/search-image?query=Isometric%20miniature%20business%20analytics%20workspace%20with%20holographic%20floating%20chart%20displays%20blue%20orange%20color%20scheme%20single%20scene%20no%20collage%20CGI%20render%20style%20tiny%20people%20working%20professional%20data%20center&width=800&height=850&seq=p6-final-v5&orientation=portrait"
                alt="AI评课数据仪表盘"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-4 right-4">
                <div className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/50 shadow-lg">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-sm">
                    <i className="ri-brain-line text-white text-sm" />
                  </div>
                  <div>
                    <div className="text-sm font-body font-bold text-foreground-800">AI评课系统</div>
                    <div className="text-xs text-foreground-500 font-body">同时服务全局视野与个体成长</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧两张卡片 */}
          <div className="lg:col-span-7 flex flex-col gap-5">

            <div className={`flex-1 rounded-2xl overflow-hidden glass-liquid transition-all duration-700 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="h-1 bg-gradient-to-r from-primary-500 to-primary-300" />
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 border border-primary-200/50 flex items-center justify-center">
                    <i className="ri-building-2-line text-primary-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-800">管理者视角</h3>
                    <span className="text-xs font-body font-semibold text-primary-500">看全局</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {managerItems.map((item) => (
                    <div key={item.label} className="p-3 rounded-xl glass border border-background-200/40 hover:-translate-y-0.5 hover:border-primary-200/50 hover:shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-all duration-300 cursor-default">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-7 h-7 rounded-md bg-primary-50 border border-primary-200/30 flex items-center justify-center">
                          <i className={`${item.icon} text-primary-500 text-xs`} />
                        </div>
                        <span className="text-sm font-body font-bold text-foreground-800">{item.label}</span>
                      </div>
                      <p className="text-xs text-foreground-500 font-body leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t border-background-200/30">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-400 font-body">系统输出：</span>
                    {['趋势图', '分档比例', '质量地图'].map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-lg bg-primary-50 border border-primary-200/30 text-xs font-body font-medium text-primary-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex-1 rounded-2xl overflow-hidden glass-liquid transition-all duration-700 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '580ms' }}
            >
              <div className="h-1 bg-gradient-to-r from-gold-400 to-gold-300" />
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gold-100/80 border border-gold-300/40 flex items-center justify-center">
                    <i className="ri-graduation-cap-line text-gold-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-800">优秀教师视角</h3>
                    <span className="text-xs font-body font-semibold text-gold-600">看方法</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {teacherItems.map((item) => (
                    <div key={item.label} className="p-3 rounded-xl glass border border-background-200/40 hover:-translate-y-0.5 hover:border-gold-200/50 hover:shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-all duration-300 cursor-default">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-7 h-7 rounded-md bg-gold-50 border border-gold-300/30 flex items-center justify-center">
                          <i className={`${item.icon} text-gold-500 text-xs`} />
                        </div>
                        <span className="text-sm font-body font-bold text-foreground-800">{item.label}</span>
                      </div>
                      <p className="text-xs text-foreground-500 font-body leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t border-background-200/30">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-400 font-body">系统输出：</span>
                    {['证据卡', '案例建议', '成长跟踪'].map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-lg bg-gold-50 border border-gold-200/40 text-xs font-body font-medium text-gold-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}